import * as vp from 'vopay';
import { FunctionComponent, JSX } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const { Table, Box, SubTitle, Badge, formattedCurrency } = vp;

type TransactionInfoProps = {
  transaction: vp.ACHWithdrawTransactionResponse;
  Notes: string;
};

type DataProps = {
  transactionId: string;
  hasErrorInfo: boolean;
  failureReason: string;
};

type FC = FunctionComponent<TransactionInfoProps>;

type KeyValuePair = {
  display: string;
  value: string | JSX.Element;
  visible?: boolean;
};

type TablePropsHandlers = {
  transactionDetail: () => vp.TableProps;
  accountHolderDetails: () => vp.TableProps;
  bankAccountDetails: () => vp.TableProps;
  errorInfo: () => vp.TableProps;
};

const ACHFundingTransactionInfo: FC = (props: TransactionInfoProps) => {
  const { state } = useLocation<DataProps>();
  const { transaction, Notes } = props;
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.ACHWithdrawalTransactionInfo',
  });
  const { t: tGlobal } = useTranslation('translations');

  return (
    <>
      <Box mt="lg" mb="md">
        <SubTitle subTitle={t('transactionDetails') || ''} />
      </Box>
      <Table {...(getTableProps('transactionDetail') || '')} />
      <Box mt="lg" mb="md">
        <SubTitle subTitle={t('accountHolderDetails') || ''} />
      </Box>
      <Table {...(getTableProps('accountHolderDetails') || '')} />
      <Box mt="lg" mb="md">
        <SubTitle subTitle={t('bankAccountDetails') || ''} />
      </Box>
      <Table {...(getTableProps('bankAccountDetails') || '')} />
      {state?.hasErrorInfo && (
        <>
          <Box mt="lg" pt="md" mb="md">
            <SubTitle subTitle={t('errorInfo') || ''} />
          </Box>
          <Table {...getTableProps('errorInfo')} />
        </>
      )}
    </>
  );

  function getTableProps(handler: keyof TablePropsHandlers): vp.TableProps {
    const handlers: TablePropsHandlers = {
      transactionDetail: () => {
        const transactionDetailsPairs: KeyValuePair[] = [
          {
            display: t('transactionId'),
            value: transaction.TransactionID,
          },
          {
            display: t('transactionStatus'),
            value: (
              <Badge
                label={tGlobal(`Transaction.TransactionHistory.statuses.${transaction.TransactionStatus}`, {
                  defaultValue: transaction.TransactionStatus,
                })}
                hasIcon
                color={vp.GetColorStatus(transaction.TransactionStatus)}
              />
            ),
          },
          {
            display: t('transactionDate'),
            value: transaction.TransactionDateTime,
          },
          {
            display: t('lastModified'),
            value: transaction.LastModified,
          },
          {
            display: t('holdReleaseDate'),
            value: transaction.HoldReleaseDateTime,
          },
          {
            display: t('type'),
            value: t('ACHWithdrawal') || '',
          },
          {
            display: t('amount'),
            value: `$${formattedCurrency(transaction.Amount)}`,
          },
          {
            display: t('holdAmount'),
            value: `$${formattedCurrency(transaction.HoldAmount)}`,
          },
          {
            display: t('currency'),
            value: transaction.Currency,
          },
          {
            display: t('notes'),
            value: Notes,
          },
        ];

        const transactionDetailsProps: vp.TableProps = {
          name: 'transaction-details',
          variant: 'pair',
          columns: {
            detailKey: { header: t('transactionDetail') || '' },
            detailValue: { header: '' },
          },
          rows: transactionDetailsPairs.map(({ display, value }) => ({
            detailKey: display,
            detailValue: value,
          })),
        };

        return transactionDetailsProps;
      },
      accountHolderDetails: () => {
        const accountHolderDetailsPairs: KeyValuePair[] = [
          {
            display: t('name'),
            value: `${transaction.FirstName} ${transaction.LastName}`,
            visible: !!transaction.FirstName,
          },
          {
            display: t('companyName'),
            value: transaction.CompanyName,
            visible: !!transaction.CompanyName,
          },
          {
            display: t('address'),
            value: `${transaction.Address1} ${transaction.Address2}`,
            visible: transaction.Address1 !== '',
          },
          {
            display: t('city'),
            value: transaction.City,
            visible: transaction.City !== '',
          },
          {
            display: t('postalCodeZipcode'),
            value: transaction['PostalCode/Zipcode'],
            visible: !!transaction['PostalCode/Zipcode'],
          },
          {
            display: t('provinceState'),
            value: transaction['Province/State'],
            visible: !!transaction['Province/State'],
          },
          {
            display: t('country'),
            value: transaction.Country,
            visible: true,
          },
          {
            display: t('primaryContact'),
            value: transaction.PhoneNumber,
            visible: transaction.PhoneNumber !== '',
          },
        ];

        const accountHolderDetailsProps: vp.TableProps = {
          name: 'account-holder-details',
          variant: 'pair',
          columns: {
            detailKey: { header: t('address') || '' },
            detailValue: { header: '' },
          },
          rows: accountHolderDetailsPairs
            .filter(({ visible }) => visible)
            .map(({ display, value }) => ({
              detailKey: display,
              detailValue: value,
            })),
        };

        return accountHolderDetailsProps;
      },
      bankAccountDetails: () => {
        const bankingInformationPairs: KeyValuePair[] = [
          {
            display: t('abaRoutingNumber'),
            value: transaction.ABARoutingNumber,
          },
          {
            display: t('accountNumber'),
            value: transaction.AccountNumber,
          },
        ];

        const bankingInformationProps: vp.TableProps = {
          name: 'banking-information',
          variant: 'pair',
          columns: {
            detailKey: { header: t('bankingInformation') || '' },
            detailValue: { header: '' },
          },
          rows: bankingInformationPairs.map(({ display, value }) => ({
            detailKey: display,
            detailValue: value,
          })),
        };

        return bankingInformationProps;
      },
      errorInfo: () => {
        const ErrorInfoPairs: KeyValuePair[] = [
          {
            display: t('failureDateTime'),
            value: transaction.LastModified,
          },
          {
            display: t('amount'),
            value: `$${formattedCurrency(transaction.Amount)}`,
          },
          {
            display: t('failureReason'),
            value: state.failureReason,
          },
        ];

        const ErrorInfoProps: vp.TableProps = {
          name: 'error-info',
          variant: 'pair',
          columns: {
            detailKey: { header: t('errorInformation') || '' },
            detailValue: { header: '' },
          },
          rows: ErrorInfoPairs.map(({ display, value }) => ({
            detailKey: display,
            detailValue: value,
          })),
        };

        return ErrorInfoProps;
      },
    };

    return handlers[handler]();
  }
};

export default ACHFundingTransactionInfo;
